import type { AudioPlayer, PlayOptions } from './audioPlayer';
import type { SoundCue, SoundDef } from '../../core/config/audioManifest';
import { AUDIO_MANIFEST } from '../../core/config/audioManifest';

// Web Audio implementation (port of the legacy sound.js, which was written but
// never loaded). Decodes manifest files up front; if a file fails to load it
// synthesises an oscillator buffer so cues always make *some* sound.
export class WebAudioPlayer implements AudioPlayer {
  private ctx: AudioContext | null = null;
  private buffers = new Map<SoundCue, AudioBuffer>();
  private muted = false;

  constructor() {
    try {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctor();
      void this.loadAll();
    } catch {
      this.ctx = null;
    }
  }

  unlock(): void {
    if (this.ctx?.state === 'suspended') void this.ctx.resume();
  }

  play(cue: SoundCue, opts: PlayOptions = {}): void {
    if (this.muted || !this.ctx) return;
    this.unlock();
    const buffer = this.buffers.get(cue);
    if (!buffer) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = opts.rate ?? 1;
    const gain = this.ctx.createGain();
    gain.gain.value = AUDIO_MANIFEST[cue].volume * (opts.volume ?? 1);
    src.connect(gain).connect(this.ctx.destination);
    src.start(0);
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    return this.muted;
  }

  private async loadAll(): Promise<void> {
    const entries = Object.entries(AUDIO_MANIFEST) as [SoundCue, SoundDef][];
    await Promise.all(
      entries.map(async ([cue, def]) => {
        if (def.url === null) {
          this.buffers.set(cue, this.synthesize(def));
          return;
        }
        try {
          const res = await fetch(def.url);
          if (!res.ok) throw new Error(String(res.status));
          const buf = await this.ctx!.decodeAudioData(await res.arrayBuffer());
          this.buffers.set(cue, buf);
        } catch {
          this.buffers.set(cue, this.synthesize(def));
        }
      }),
    );
  }

  private synthesize(def: SoundDef): AudioBuffer {
    const ctx = this.ctx!;
    const { type, freq, duration, sweep } = def.fallback;
    const rate = ctx.sampleRate;
    const buffer = ctx.createBuffer(1, Math.floor(rate * duration), rate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < buffer.length; i++) {
      const t = i / rate;
      const nt = t / duration;
      const envelope = Math.sin(nt * Math.PI);
      const f = freq + sweep * nt;
      let sample = 0;
      switch (type) {
        case 'square': sample = Math.sin(f * t * Math.PI * 2) > 0 ? 1 : -1; break;
        case 'sawtooth': sample = 2 * ((f * t) % 1) - 1; break;
        case 'triangle': sample = 2 * Math.abs(2 * ((f * t) % 1) - 1) - 1; break;
        default: sample = Math.sin(f * t * Math.PI * 2);
      }
      data[i] = sample * envelope * 0.5;
    }
    return buffer;
  }
}
